<script>
import { useAlert } from 'dashboard/composables';
import FormButton from 'v3/components/Form/Button.vue';
import UserProfilePicture from './UserProfilePicture.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
export default {
  components: {
    FormButton,
    UserProfilePicture,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    },
    customLine: {
      type: String,
      default: '',
    },
    emailSignaturePictureUrl: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'updateUser',
    'updateEmailSignaturePicture',
    'deleteEmailSignaturePicture',
  ],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      userPosition: this.position,
      userCustomLine: this.customLine,
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userPosition: {},
    userCustomLine: {},
    userEmail: {
      required,
      email,
    },
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    position: {
      handler(value) {
        this.userPosition = value;
      },
      immediate: true,
    },
    customLine: {
      handler(value) {
        this.userCustomLine = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
  },
  methods: {
    async updateUser() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      this.$emit('updateUser', {
        name: this.userName,
        displayName: this.userDisplayName,
        position: this.userPosition,
        customLine: this.userCustomLine,
        email: this.userEmail,
      });
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateUser('profile')">
    <woot-input
      v-model="userName"
      :styles="inputStyles"
      :class="{ error: v$.userName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? $t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <woot-input
      v-model="userDisplayName"
      :styles="inputStyles"
      :class="{ error: v$.userDisplayName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :error="`${
        v$.userDisplayName.$error
          ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    />
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      :styles="inputStyles"
      :class="{ error: v$.userEmail.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :error="`${
        v$.userEmail.$error ? $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />
    <woot-input
      v-model="userPosition"
      :styles="inputStyles"
      :class="{ error: v$.userPosition.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.POSITION.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.POSITION.PLACEHOLDER')"
      :error="`${
        v$.userPosition.$error ? $t('PROFILE_SETTINGS.FORM.POSITION.ERROR') : ''
      }`"
      @input="v$.userPosition.$touch"
      @blur="v$.userPosition.$touch"
    />
    <woot-input
      v-model="userCustomLine"
      :styles="inputStyles"
      :class="{ error: v$.userCustomLine.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.CUSTOM_LINE.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.CUSTOM_LINE.PLACEHOLDER')"
      :error="`${
        v$.userCustomLine.$error
          ? $t('PROFILE_SETTINGS.FORM.CUSTOM_LINE.ERROR')
          : ''
      }`"
      @input="v$.userCustomLine.$touch"
      @blur="v$.userCustomLine.$touch"
    />
    <UserProfilePicture
      :src="emailSignaturePictureUrl"
      :name="userName"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_PICTURE.LABEL')"
      :hint="$t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_PICTURE.HINT')"
      @change="$emit('updateEmailSignaturePicture', $event)"
      @delete="$emit('deleteEmailSignaturePicture')"
    />
    <FormButton
      type="submit"
      color-scheme="primary"
      variant="solid"
      size="large"
    >
      {{ $t('PROFILE_SETTINGS.BTN_TEXT') }}
    </FormButton>
  </form>
</template>
